import React, { useState } from "react";
import { ChevronRight, Eye } from "lucide-react";
import { CATEGORIES, TOP_CATEGORIES, DESIGNS } from "../data/mock";

function DesignCard({ design, onGetQuote }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className="relative rounded-xl overflow-hidden group cursor-pointer shadow-sm hover:shadow-xl transition-all duration-300"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`design-card-${design.id}`}
    >
      <div className="aspect-[4/3] overflow-hidden">
        <img
          src={design.image}
          alt={design.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
        <h3 className="text-white font-semibold text-sm mb-2 leading-tight">{design.title}</h3>
        <div className="flex gap-2">
          <button
            onClick={onGetQuote}
            className="flex-1 bg-[#C9A84C] text-white text-xs font-semibold py-2 rounded-lg hover:bg-[#b8933e] transition-colors"
          >
            Get Quote
          </button>
          <button className="bg-white/20 backdrop-blur-sm text-white text-xs font-medium py-2 px-3 rounded-lg flex items-center gap-1 hover:bg-white/30">
            <Eye size={12} />
            Details
          </button>
        </div>
      </div>
      <div className="p-3 bg-white">
        <p className="text-sm font-medium text-[#1a1a1a] truncate">{design.title}</p>
        <span className="inline-block mt-1 text-xs text-[#C9A84C] font-medium">{design.tag}</span>
      </div>
    </div>
  );
}

export function DesignGallery({ onGetQuote }) {
  const [activeCategory, setActiveCategory] = useState("all");
  const [showAll, setShowAll] = useState(false);

  const filtered = activeCategory === "all"
    ? DESIGNS
    : DESIGNS.filter((d) => d.category === activeCategory);

  const displayed = showAll ? filtered : filtered.slice(0, 8);

  return (
    <section id="designs" className="py-16 bg-[#FAF8F5]" data-testid="design-gallery">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <div className="text-center mb-10">
          <span className="text-[#C9A84C] text-sm font-semibold tracking-widest uppercase">Our Portfolio</span>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#1a1a1a] mt-2 mb-3">
            Home Interior Designs
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Explore our curated collection of stunning interior designs crafted for Hyderabad homes
          </p>
        </div>

        {/* Top Categories Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-10">
          {TOP_CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => { setActiveCategory(cat.id); setShowAll(false); }}
              className={`relative rounded-xl overflow-hidden group transition-all ${
                activeCategory === cat.id ? "ring-2 ring-[#C9A84C]" : ""
              }`}
              data-testid={`top-category-${cat.id}`}
            >
              <div className="aspect-square">
                <img src={cat.image} alt={cat.label} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />
              </div>
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-end p-2">
                <span className="text-white text-xs font-semibold leading-tight">{cat.label}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Category filter tabs */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-8 scrollbar-hide">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              onClick={() => { setActiveCategory(cat.id); setShowAll(false); }}
              className={`flex-shrink-0 text-sm font-medium px-5 py-2 rounded-full border transition-all ${
                activeCategory === cat.id
                  ? "bg-[#1a1a1a] text-white border-[#1a1a1a]"
                  : "bg-white text-[#2D2D2D] border-gray-200 hover:border-[#C9A84C] hover:text-[#C9A84C]"
              }`}
              data-testid={`filter-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Design Grid */}
        {displayed.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p>No designs yet for this category. <span className="text-[#C9A84C] cursor-pointer" onClick={() => setActiveCategory("all")}>View all designs</span></p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {displayed.map((d) => (
              <DesignCard key={d.id} design={d} onGetQuote={onGetQuote} />
            ))}
          </div>
        )}

        {filtered.length > 8 && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAll(!showAll)}
              className="inline-flex items-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] font-semibold px-8 py-3 rounded-full hover:bg-[#1a1a1a] hover:text-white transition-all"
              data-testid="view-more-btn"
            >
              {showAll ? "Show Less" : `View More ${activeCategory === "all" ? "" : CATEGORIES.find(c => c.id === activeCategory)?.label} Designs`}
              <ChevronRight size={16} className={showAll ? "rotate-90" : ""} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
